import {TransportError} from '@microsoft/agent-host-protocol/client'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  connectToEnvironment,
  environmentConnectApiPath,
  environmentReconnectApiPath,
  type MCFetcher,
} from './mission-control/mission-control-environment-client'
import type {RelayConnectionRuntimeOptions} from './relay-connection-runtime'
import {Reassembler} from './webpubsub/chunking'
import {parseInbound} from './webpubsub/framing'
import type {WebSocketLike, WpsMessageEvent, WpsCloseEvent} from './webpubsub/wps-transport'

const errorCodes = [
  'environment_compute_missing',
  'environment_compute_terminal',
  'environment_unreachable',
  'copilot_license_required',
] as const

export interface RelayTestConnectResponse {
  /** 202 simulates waking; 400-599 simulate HTTP errors. Successful credentials are never accepted. */
  status: number
  code?: (typeof errorCodes)[number]
}

export interface RelayTestHostClosedOptions {
  /**
   * The ended host connection's generation. `'current'` (the default) uses the
   * one the host stamped on its answer to the socket's latest handshake, or none
   * if it stamped none; `'stale'` uses a different one.
   */
  generation?: number | 'current' | 'stale'
}

export interface RelayTestControls {
  disconnect(taskId: string): void
  simulateHostClosed(taskId: string, options?: RelayTestHostClosedOptions): void
  blockConnections(taskId: string): void
  restoreConnections(taskId: string): void
  dropIncomingMessages(taskId: string, enabled: boolean): void
  setConnectResponse(taskId: string, response: RelayTestConnectResponse | null): void
  reset(taskId: string): void
}

declare global {
  interface Window {
    __ahpRelayTestControls?: RelayTestControls
  }
}

interface Faults {
  blocked?: boolean
  dropIncoming?: boolean
  response?: RelayTestConnectResponse
}

/** What a task's socket has shown of its lanes and its host connection. */
interface Lane {
  toClientGroup?: string
  /** Wire id of the latest `initialize` or `reconnect` the socket sent, until it is answered. */
  handshakeId?: number
  /** Generation the host stamped on its answer to that handshake. */
  generation?: number
  /** Decodes inbound envelopes while a handshake waits for its answer. */
  readonly reassembler: Reassembler
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/** Records the `to-client` group a socket joins and the wire id of its latest handshake. */
function observeOutbound(lane: Lane, data: string): void {
  if (!/"joinGroup"|"initialize"|"reconnect"/.test(data)) return
  try {
    const frame: unknown = JSON.parse(data)
    if (!isObject(frame)) return
    if (frame.type === 'joinGroup' && typeof frame.group === 'string') {
      if (frame.group.endsWith('.to-client')) lane.toClientGroup = frame.group
    } else if (frame.type === 'sendToGroup' && isObject(frame.data) && frame.data.kind === 'message') {
      // Handshake requests stay far below the chunking threshold, so they always travel whole.
      const request = frame.data.data
      if (
        isObject(request) &&
        (request.method === 'initialize' || request.method === 'reconnect') &&
        typeof request.id === 'number'
      ) {
        lane.handshakeId = request.id
      }
    }
  } catch {
    // Observation must never stop the frame from being sent.
  }
}

/**
 * Records the generation of the host's answer to the latest handshake, as the
 * transport does. Messages from another host connection never change it.
 */
function observeInbound(lane: Lane, data: unknown): void {
  if (typeof data !== 'string' || lane.handshakeId === undefined) return
  try {
    const result = parseInbound(JSON.parse(data), {reassembler: lane.reassembler})
    if (result.kind !== 'payload' && result.kind !== 'payloads') return
    const payloads = result.kind === 'payload' ? [result.payload] : result.payloads
    if (payloads.some(payload => isObject(payload) && !('method' in payload) && payload.id === lane.handshakeId)) {
      lane.generation = result.generation
      lane.handshakeId = undefined
    }
  } catch {
    // The transport reports malformed frames itself.
  }
}

/** Owns no runtime or UI state; all failures enter through the existing transport and HTTP seams. */
export function createRelayTestControls() {
  const faults = new Map<string, Faults>()
  const sockets = new Map<string, Set<WebSocketLike>>()
  const lanes = new WeakMap<WebSocketLike, Lane>()

  function validateTaskId(taskId: string): void {
    if (typeof taskId !== 'string' || taskId.length === 0 || taskId.length > 200 || taskId.includes('\u0000')) {
      throw new Error('Relay test controls require an explicit task id')
    }
  }

  function update(taskId: string, change: Partial<Faults>): void {
    validateTaskId(taskId)
    const next = {...faults.get(taskId), ...change}
    if (!next.blocked && !next.dropIncoming && !next.response) faults.delete(taskId)
    else faults.set(taskId, next)
  }

  function checkBlocked(taskId: string): void {
    if (faults.get(taskId)?.blocked) throw new TransportError('io', 'Connection blocked by relay test controls')
  }

  const controls: RelayTestControls = {
    disconnect(taskId) {
      validateTaskId(taskId)
      const liveSockets = sockets.get(taskId)
      if (!liveSockets?.size) throw new Error('No live relay socket for this task')
      for (const socket of [...liveSockets]) socket.close()
    },
    simulateHostClosed(taskId, options = {}) {
      validateTaskId(taskId)
      const requested = options.generation ?? 'current'
      if (
        requested !== 'current' &&
        requested !== 'stale' &&
        !(typeof requested === 'number' && Number.isSafeInteger(requested) && requested > 0)
      ) {
        throw new Error('Expected a positive safe integer generation, "current", or "stale"')
      }
      const liveSockets = sockets.get(taskId)
      if (!liveSockets?.size) throw new Error('No live relay socket for this task')
      const notices = [...liveSockets].map(socket => {
        const lane = lanes.get(socket)
        if (!lane?.toClientGroup) throw new Error('The relay socket has not joined its to-client lane')
        let generation = requested === 'current' ? lane.generation : requested
        if (generation === 'stale') {
          if (lane.generation === undefined) throw new Error('The current host connection has no generation')
          generation = lane.generation > 1 ? lane.generation - 1 : lane.generation + 1
        }
        // Without a sequenceId, as the transport neither acknowledges nor deduplicates it.
        const frame = {
          type: 'message',
          from: 'group',
          group: lane.toClientGroup,
          dataType: 'json',
          data: {kind: 'closed', ...(generation === undefined ? {} : {generation})},
        }
        return {socket, data: JSON.stringify(frame)}
      })
      for (const {socket, data} of notices) socket.onmessage?.({data})
    },
    blockConnections: taskId => update(taskId, {blocked: true}),
    restoreConnections: taskId => update(taskId, {blocked: false}),
    dropIncomingMessages(taskId, enabled) {
      if (typeof enabled !== 'boolean') throw new Error('Incoming message drop requires a boolean')
      update(taskId, {dropIncoming: enabled})
    },
    setConnectResponse(taskId, response) {
      validateTaskId(taskId)
      if (
        response !== null &&
        (!response ||
          !Number.isInteger(response.status) ||
          (response.status !== 202 && (response.status < 400 || response.status > 599)) ||
          (response.code !== undefined && !errorCodes.includes(response.code)) ||
          (response.status === 202 && response.code !== undefined))
      ) {
        throw new Error('Expected a waking response (202) or HTTP error (400-599) with an optional supported code')
      }
      update(taskId, {response: response === null ? undefined : {status: response.status, code: response.code}})
    },
    reset(taskId) {
      validateTaskId(taskId)
      faults.delete(taskId)
    },
  }

  function wrapOptions(taskId: string, options: RelayConnectionRuntimeOptions): RelayConnectionRuntimeOptions {
    validateTaskId(taskId)
    const mcConnect = options.mcConnect
    const fetcher: MCFetcher = async (path, fetchOptions) => {
      const isConnect = path === environmentConnectApiPath(options.environmentId, options.sessionId)
      const reconnectPrefix = environmentReconnectApiPath(options.environmentId, '').split('?')[0]
      if (isConnect || path.startsWith(`${reconnectPrefix}?client_id=`)) {
        fetchOptions?.signal?.throwIfAborted()
        checkBlocked(taskId)
        const response = faults.get(taskId)?.response
        if (response) {
          return Response.json(response.status === 202 ? {environment_status: 'waking'} : {code: response.code}, {
            status: response.status,
          })
        }
      }
      if (!options.fetcher) throw new Error('Relay test controls require a Mission Control fetcher')
      return options.fetcher(path, fetchOptions)
    }

    return {
      ...options,
      fetcher,
      // Preconnected credentials normally bypass HTTP. Keep that behavior unless a fault targets this task.
      ...(mcConnect
        ? {
            mcConnect: fetchOptions => {
              fetchOptions?.signal?.throwIfAborted()
              checkBlocked(taskId)
              return faults.get(taskId)?.response
                ? connectToEnvironment(options.environmentId, options.sessionId, fetcher, {
                    ...fetchOptions,
                    onProgress: options.onConnectionProgress,
                  })
                : mcConnect(fetchOptions)
            },
          }
        : {}),
      webSocketFactory: (url, subprotocol) => {
        checkBlocked(taskId)
        const socket = options.webSocketFactory?.(url, subprotocol) ?? new WebSocket(url, subprotocol)
        const taskSockets = sockets.get(taskId) ?? new Set<WebSocketLike>()
        const lane: Lane = {reassembler: new Reassembler()}
        const tracked: WebSocketLike = {
          send: data => {
            observeOutbound(lane, data)
            socket.send(data)
          },
          close: () => socket.close(),
          onopen: null,
          onmessage: null,
          onclose: null,
          onerror: null,
        }
        lanes.set(tracked, lane)
        taskSockets.add(tracked)
        sockets.set(taskId, taskSockets)
        socket.onopen = () => tracked.onopen?.()
        socket.onmessage = (event: WpsMessageEvent) => {
          if (faults.get(taskId)?.dropIncoming) return
          observeInbound(lane, event.data)
          tracked.onmessage?.(event)
        }
        socket.onerror = (event: unknown) => tracked.onerror?.(event)
        socket.onclose = (event: WpsCloseEvent) => {
          taskSockets.delete(tracked)
          if (taskSockets.size === 0 && sockets.get(taskId) === taskSockets) sockets.delete(taskId)
          tracked.onclose?.(event)
        }
        return tracked
      },
    }
  }

  return {controls, wrapOptions}
}

export function installRelayTestControls(): ReturnType<typeof createRelayTestControls> | undefined {
  if (typeof window === 'undefined' || !isFeatureEnabled('copilot_ahp_relay_test_controls')) return
  const controller = createRelayTestControls()
  window.__ahpRelayTestControls = controller.controls
  return controller
}
